// The capability list, mirroring backend/common/permissions.py (Build Book §06).
//
// A contract test asserts these two export equal sets. They drift the moment
// someone adds a capability on one side only, and the symptom is a nav item
// that shows for a role the server then 403s.
//
// The nav gate is a courtesy, not a control: /auth/me is read once at boot, so
// a cashier demoted mid-shift keeps the old menu until reload. The server is
// the control.

export const CAP = {
  SELL: 'sell',
  VIEW_CUSTOMERS: 'view_customers',
  MANAGE_PRODUCTS: 'manage_products',
  MANAGE_PARTIES: 'manage_parties',
  VIEW_COST: 'view_cost',
  PURCHASES: 'purchases',
  OVERRIDE_CREDIT_LIMIT: 'override_credit_limit',
  OVERRIDE_PRICE_FLOOR: 'override_price_floor',
  SET_OPENING_STOCK: 'set_opening_stock',
  // Owner AND manager, unlike SET_OPENING_STOCK above. An adjustment is an
  // append-only signed movement with a reason on it; the opening count is a
  // restatement of the base every later figure is checked against.
  ADJUST_STOCK: 'adjust_stock',
  MANAGE_CREDIT_TERMS: 'manage_credit_terms',
  MANAGE_STAFF: 'manage_staff',
  SETTINGS: 'settings',
  // Same-day cancel of a posted bill or supplier bill. The owner's alone — a
  // manager may push a sale past a credit limit, which leaves a document behind
  // to read; cancelling takes the document out of every total the shop reports.
  CANCEL_DOCUMENT: 'cancel_document',
  // Bulk import of products with opening stock, suppliers and customers. The
  // owner's alone; a platform super user reaches it by impersonating the owner.
  BULK_IMPORT: 'bulk_import',
  // Delete a product that never moved. The owner's alone; anyone with
  // MANAGE_PRODUCTS can still discontinue one, which keeps it.
  DELETE_PRODUCTS: 'delete_products',
  // The same for a customer or supplier with no history. The owner's alone.
  DELETE_PARTIES: 'delete_parties',
  // Write down a running cost — transport, hamali, tea, rent (V020). Every
  // tenant role; a cashier sees only the lines they entered.
  RECORD_EXPENSES: 'record_expenses',
  // Delete any expense line, any day. The owner's alone; anyone can delete a
  // line they entered themselves on the same day.
  DELETE_EXPENSES: 'delete_expenses',
  // Check the day's money — count the drawer, confirm UPI and bank totals
  // (V021). Owner and manager; never the cashier, whose drawer is counted.
  CHECK_DAY: 'check_day',
  PLATFORM: 'platform',
}

export const ALL_CAPABILITIES = Object.values(CAP)

/**
 * Fails closed. An unknown capability, a missing array, or an unresolved
 * /auth/me all return false — and it never falls back to inspecting the role,
 * because a role check here is a second source of truth that will disagree
 * with the server's matrix eventually.
 */
export function can(capabilities, capability) {
  if (!Array.isArray(capabilities)) return false
  if (!ALL_CAPABILITIES.includes(capability)) return false
  return capabilities.includes(capability)
}
